"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, X } from "lucide-react";
import { CustomerFilterCombobox } from "./CustomerFilterCombobox";

/** A selectable package or plan, already namespaced by its group. */
export interface PackageFilterOption {
  /** "package:<id>" or "plan:<id>" -- see BillingManagement for why. */
  value: string;
  label: string;
}

interface BillingFiltersProps {
  filterStatus: string;
  filterPaymentMethod: string;
  filterCustomerId: string;
  filterPackage: string;
  filterServiceType: string;
  startDate: string;
  endDate: string;
  /** One-time packages, already filtered to the selected service type. */
  packageOptions: PackageFilterOption[];
  /** Subscription plans, already filtered to the selected service type. */
  planOptions: PackageFilterOption[];
  /**
   * True when a package or plan is selected. The package implies its service
   * type, so the control is locked rather than left free to contradict it.
   */
  serviceTypeLocked: boolean;
  onStatusChange: (value: string) => void;
  onPaymentMethodChange: (value: string) => void;
  onCustomerChange: (value: string) => void;
  onPackageChange: (value: string) => void;
  onServiceTypeChange: (value: string) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onClearDates: () => void;
  onClearAll: () => void;
}

export function BillingFilters({
  filterStatus,
  filterPaymentMethod,
  filterCustomerId,
  filterPackage,
  filterServiceType,
  startDate,
  endDate,
  packageOptions,
  planOptions,
  serviceTypeLocked,
  onStatusChange,
  onPaymentMethodChange,
  onCustomerChange,
  onPackageChange,
  onServiceTypeChange,
  onStartDateChange,
  onEndDateChange,
  onClearDates,
  onClearAll,
}: BillingFiltersProps) {
  const hasDateFilter = startDate || endDate;
  const hasAnyFilter =
    hasDateFilter ||
    filterStatus !== "all" ||
    filterPaymentMethod !== "all" ||
    filterServiceType !== "all" ||
    filterPackage !== "all" ||
    Boolean(filterCustomerId);

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div>
            <Label>Filter by Customer</Label>
            <CustomerFilterCombobox
              value={filterCustomerId}
              onChange={onCustomerChange}
            />
          </div>
          <div>
            <Label>Filter by Package/Plan</Label>
            <Select value={filterPackage} onValueChange={onPackageChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Packages</SelectItem>
                {packageOptions.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>One-time Packages</SelectLabel>
                    {packageOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {planOptions.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Subscription Plans</SelectLabel>
                    {planOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Filter by Service Type</Label>
            <Select
              value={filterServiceType}
              onValueChange={onServiceTypeChange}
              disabled={serviceTypeLocked}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Service Types</SelectItem>
                <SelectItem value="one-time">Appointment</SelectItem>
                <SelectItem value="subscription">Subscription</SelectItem>
              </SelectContent>
            </Select>
            {serviceTypeLocked && (
              <p className="mt-1 text-xs text-muted-foreground">
                Set by the selected package
              </p>
            )}
          </div>
          <div>
            <Label>Filter by Payment Status</Label>
            <Select value={filterStatus} onValueChange={onStatusChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Filter by Payment Method</Label>
            <Select
              value={filterPaymentMethod}
              onValueChange={onPaymentMethodChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank-transfer">Bank Transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              From Date
            </Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              To Date
            </Label>
            <div className="flex gap-2">
              <Input
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
                className="flex-1"
              />
              {hasDateFilter && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onClearDates}
                  title="Clear date filter"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {hasAnyFilter && (
          <div className="mt-4 flex justify-end">
            <Button variant="ghost" size="sm" onClick={onClearAll}>
              <X className="mr-1 h-4 w-4" />
              Clear all filters
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
